import { Link } from 'react-router-dom';
import { Car, ArrowRight, Info, AlertTriangle, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BrandGrid from '@/components/smartcar/BrandGrid';
import RelatedLinks from '@/components/RelatedLinks';
import CustomerPageIntro from '@/components/redesign/CustomerPageIntro';
import { STABLE_BRANDS, BETA_BRANDS, BRAND_LIST_UPDATED } from '@/components/smartcar/carBrands';

const SERIF = { fontFamily: '"Playfair Display", Georgia, serif' };

export default function EnSupportedCarBrands() {
  return (
    <div className="customer-service-page">
      <CustomerPageIntro eyebrow="Car compatibility" title="Which car brands have we confirmed with Saving Mode?">
        <p>Saving Mode always works through all three parts: your charger, your car and the enkel app. Below are the brands we have confirmed working today, and which ones are in beta. Other brands may also work – we just can't guarantee it. The list was last reviewed with our app team in {BRAND_LIST_UPDATED.en}.</p>
      </CustomerPageIntro>

      {/* Supported */}
      <section className="py-12">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="text-xl font-bold text-[#1F2E33] mb-1" style={SERIF}>Confirmed car brands</h2>
          <p className="text-sm text-gray-500 mb-5">We have confirmed these brands running normally for our customers.</p>
          <BrandGrid brands={STABLE_BRANDS} />
        </div>
      </section>

      {/* Beta */}
      <section className="py-12 bg-[#F4F1EA]">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="text-xl font-bold text-[#1F2E33] mb-1" style={SERIF}>Beta – can be unreliable</h2>
          <p className="text-sm text-gray-500 mb-5">
            We have customers connected on these brands, but we also see accounts where the connection
            to the car fails or drops out. We're working on them together with our provider.
          </p>
          <BrandGrid brands={BETA_BRANDS} beta />

          <div className="mt-6 flex gap-3 p-4 rounded-xl bg-white border border-[#D4A62A]/35">
            <AlertTriangle className="w-5 h-5 text-[#B98A17] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600 leading-relaxed">
              If your car is in beta you can still use Saving Mode – but the car may not respond, or the
              connection may need to be set up again. You can always charge as normal in the meantime, and
              your electricity plan at spot price with no markup is unaffected. Note that the price guarantee
              and charging discount require charging to actually be controlled by Saving Mode through the
              charger, the car and the app.
            </p>
          </div>
        </div>
      </section>

      {/* Not on the list */}
      <section className="py-12">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="text-xl font-bold text-[#1F2E33] mb-3" style={SERIF}>Brand not on the list?</h2>
          <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
            <p>
              It may well still work. The list covers the brands we have confirmed – it is not an exhaustive
              list of what's possible. We just can't guarantee it up front.
            </p>
            <p>
              Saving Mode always requires all three parts: a compatible charger (Zaptec, NexBlue or Easee),
              your car and the enkel app. If you're unsure about your car, ask us before you decide – we'll
              check the specific model.
            </p>
          </div>

          <div className="mt-6 flex gap-3 p-4 rounded-xl bg-white border border-[#263A43]/12">
            <Info className="w-5 h-5 text-[#5E7B8C] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600 leading-relaxed">
              Whether a car can connect depends on model, year, equipment and the manufacturer's own system,
              and can change without notice. The list is therefore indicative: it is neither a guarantee that
              your specific car can be connected, nor a rule-out of brands that aren't on it.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-b from-[#394D59] to-[#2F424D] text-white text-center">
        <div className="container mx-auto px-6 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={SERIF}>Not sure about your car?</h2>
          <p className="text-gray-300 text-sm mb-8">
            Write or call us – we'll check your model before you decide.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/en/electricity-benefits">
              <Button size="lg" className="bg-[#F1EBDD] hover:bg-[#E9DFC9] text-[#21343D] font-bold px-8 h-[54px] border-[1.5px] border-[#D4A62A]/55">
                See Saving Mode and benefits <ArrowRight className="w-5 h-5 ml-3 text-[#B58716]" />
              </Button>
            </Link>
            <Link to="/en/support">
              <Button size="lg" variant="outline" className="ee-button-secondary px-8 h-[54px]">
                <HelpCircle className="w-4 h-4 mr-2" /> Contact support
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <RelatedLinks lang="en" items={['opladere', 'smartcharge', 'elaftale']} />
    </div>
  );
}